import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	Typography,
	Dialog,
	DialogContent,
	DialogTitle,
	DialogActions,
	Stack,
	Box,
	Link,
	TextField,
	CircularProgress,
	FormHelperText,
	ButtonGroup,
} from '@mui/material';
import { colours } from '../../shared/colors';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
	useLoginMFAMutation,
	useSetupMFAQuery,
	useVerifyMFAMutation,
} from '../../services/features/authApi/authApi';
import { clearAppState } from '../../helper/authUtils';
import { GENERIC_ERROR } from '../../helper/constants';
import { useLazyGetBrokerByIdQuery } from '../../services/features/brokerApi/brokerApi';
import { MfaType } from '../../shared/types/mfaTypes';

interface MFADialogProps {
	open: boolean;
	handleClose: (event?: any) => void;
	mfaType: MfaType;
	formik: any;
}
const MFADialog = ({ open, handleClose, mfaType, formik }: MFADialogProps) => {
	const navigate = useNavigate();

	const setupMfa = mfaType === 'setup';

	const [otpCode, setOtpCode] = useState('');
	const [error, setError] = useState('');

	const [verifyMfa, { isLoading: verifyMFAIsLoading }] = useVerifyMFAMutation();
	const [loginMfa, { isLoading: loginMFAIsLoading }] = useLoginMFAMutation();

	const [emailMfa, setEmailMfa] = useState<Boolean | undefined>(() => {
		const val = localStorage.getItem('emailMfa');
		return val ? JSON.parse(val) : undefined;
	});

	const {
		data: setupMfaData,
		isLoading: setupIsLoading,
		isError,
		...setupMFAQuery
	} = useSetupMFAQuery(
		{ ...(emailMfa && { type: 'email' }), email: formik.values.email },
		{
			skip: emailMfa === undefined,
		}
	);
	const qrCode = useMemo(() => setupMfaData?.qrCode ?? '', [setupMfaData?.qrCode]);

	const [getBrokerById] = useLazyGetBrokerByIdQuery();

	const refreshAndClose = () => {
		setOtpCode('');
		setError('');
		localStorage.removeItem('emailMfa');
		clearAppState();
		handleClose();
	};

	const handleSubmit = async () => {
		if (otpCode.length > 0) {
			setError('');
			const email = formik?.values?.email;
			if (setupMfa) {
				await verifyMfa({ otpCode, ...(emailMfa && { type: 'email', email }) })
					.unwrap()
					.then((res) => {
						console.log('verifyMfa RES', res);
						if (res?.message !== 'Invalid MFA token!') {
							formik?.setFieldValue('password', '');
							formik?.setFieldValue('successMessage', res?.message);
							refreshAndClose();
						}
					})
					.catch((e) => {
						console.log('verifyMfa error', e?.data?.message);
						setError(
							e?.data?.message ?? e?.data === 'Network Error'
								? 'Network error; please try again later'
								: `Error validating MFA token. ${GENERIC_ERROR}`
						);
					});
			} else {
				await loginMfa({ email, otpCode, type: mfaType })
					.unwrap()
					.then(async (res) => {
						console.log('loginMfa response', res);
						await getBrokerById(res?.id).unwrap();
						navigate('/home');
					})
					.catch((e) => {
						console.log('loginMfa error', e);
						setError(
							e?.data?.message ?? e?.data === 'Network Error'
								? 'Network error; please try again later'
								: `Error validating MFA token. ${GENERIC_ERROR}`
						);
					});
			}
		} else {
			setError('Please enter OTP code');
		}
	};

	const toggleEmailMfa = (value: Boolean) => {
		setEmailMfa(value);
		localStorage.setItem('emailMfa', JSON.stringify(value));
	};

	const buttonSx = {
		width: '50%',
		'&:hover': {
			backgroundColor: colours.primary.hover,
			color: 'white',
		},
	};

	const verifyMfaText =
		mfaType === 'email'
			? 'Enter the code sent to your registered email address'
			: 'Enter the code shown in your Google or Microsoft Authenticator app';
	const setupMfaText =
		emailMfa === undefined
			? 'Choose an option for setting up MFA for your account'
			: emailMfa === true
			? 'Enter the code sent to your registered email address to add MFA to your account'
			: 'Scan the QR Code and add MFA to your Google or Microsoft Authenticator app';
	const mfaText = setupMfa ? setupMfaText : verifyMfaText;

	const errorText = `Error setting up ${emailMfa ? 'Email ' : ''}MFA; please try again`;

	return (
		<Dialog
			open={open}
			onClose={(e, reason) => {
				if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
					handleClose(e);
				}
			}}
			sx={{ '& .MuiPaper-root': { borderRadius: '15px' } }}
		>
			<form>
				<DialogTitle>{`${setupMfa ? 'Set Up' : 'Confirm'} MFA`}</DialogTitle>
				<DialogContent>
					{setupMfa && isError ? (
						<Stack spacing={2}>
							<FormHelperText error>{errorText}</FormHelperText>
						</Stack>
					) : (
						<Stack spacing={2}>
							<Typography variant='cardContentHeader'>{mfaText}</Typography>
							{setupMfa && setupMfaData?.qrCode && (
								<img src={`data:image/png;base64,${qrCode}`} alt='QR code' />
							)}
							{setupMfa && emailMfa === undefined && (
								<ButtonGroup variant='outlined'>
									<Button sx={buttonSx} onClick={() => toggleEmailMfa(false)}>
										Authentication App
									</Button>
									<Button sx={buttonSx} onClick={() => toggleEmailMfa(true)}>
										Email MFA
									</Button>
								</ButtonGroup>
							)}
							{(mfaType === 'verify' ||
								mfaType === 'email' ||
								(setupMfa && emailMfa !== undefined)) && (
								<TextField
									label='Authenticator OTP Code'
									autoFocus
									value={otpCode}
									onChange={(e) => {
										if (error.length > 0) {
											setError('');
										}
										setOtpCode(e.target.value);
									}}
									fullWidth
									error={error.length > 0}
								/>
							)}
							{error.length > 0 && <FormHelperText error>{error}</FormHelperText>}
						</Stack>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						variant='text'
						disabled={verifyMFAIsLoading || loginMFAIsLoading}
						onClick={(e) => {
							clearAppState();
							handleClose(e);
						}}
					>
						Cancel
					</Button>
					{!(setupMfa && emailMfa === undefined) && !isError && (
						<Button
							variant='contained'
							type='submit'
							disabled={setupIsLoading || verifyMFAIsLoading || loginMFAIsLoading}
							onClick={handleSubmit}
						>
							{setupIsLoading || verifyMFAIsLoading || loginMFAIsLoading ? (
								<CircularProgress color='inherit' />
							) : (
								`${setupMfa ? 'Set Up MFA' : 'Confirm'}`
							)}
						</Button>
					)}
				</DialogActions>
			</form>
		</Dialog>
	);
};

export default MFADialog;
